// Modal wizard de criar/editar ART (5 etapas espelhando o portal CREA-RS).
// Inclui BlocoEndereco (CEP + endereço estruturado), ListaLinhasAtividade
// (linhas: técnica × específica × qtd × und) e SecaoArt (cabeçalho de seção).
// Form helpers (formVazio, presetToForm, artToForm, artToFormParaDuplicar,
// formToPayload) ficam no fim do arquivo — são puros.
// Extraído de OperacionalContratos.tsx no refactor de 2026-05-23 (Wave 2).

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Select as AntSelect, Divider, Button as AntButton } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import type {
  Contrato, ArtFull, ArtAtividadeLinha,
  UsuarioOp, CatAtividadeSlim, OpcoesArt,
} from './types';
import {
  getToken,
  carregarUsuariosOp, carregarClientesCompletoCacheOuApi,
  carregarCatAtividades, carregarOpcoesArt, carregarUnidadesCrea,
  carregarTiposLogradouro, buscarCep,
} from './utils';
import ModalCadastrarCliente from './ModalCadastrarCliente';
import { usePerms } from '../../hooks/usePerms';
import ModalImportarArtPdf from './ModalImportarArtPdf';

// ════════════════ MODAL: WIZARD DE NOVA / EDITAR ART (CREA-RS) ════════════════
// Wizard de 5 etapas espelhando o portal CREA-RS:
//   1. Tipo de ART (cabeçalho fiscal)
//   2. Contratante (com CEP/ViaCEP)
//   3. Obra/Serviço (proprietário, endereço estruturado, valor, datas)
//   4. Atividades (linhas: técnica × específica × qtd × und)
//   5. Anexos + status CREA + observação (extra do nosso lado)

// Subcomponente: bloco de endereço estruturado com busca CEP. Compartilhado
// entre os passos "Contratante" (passo 2) e "Obra/Serviço" (passo 3).
function BlocoEndereco({ valores, onChange, comTipoLogradouro }: {
  valores: {
    cep: string; tipo_logradouro?: string; logradouro: string;
    numero: string; complemento: string; bairro: string;
    municipio: string; uf: string;
  };
  onChange: (campo: string, val: string) => void;
  comTipoLogradouro?: boolean;
}) {
  const [tiposLog, setTiposLog] = useState<string[]>([]);
  const [buscando, setBuscando] = useState(false);
  useEffect(() => { if (comTipoLogradouro) carregarTiposLogradouro().then(setTiposLog); }, [comTipoLogradouro]);

  async function preencherViaCep() {
    if (!valores.cep) { alert('Digite o CEP primeiro.'); return; }
    setBuscando(true);
    try {
      const r = await buscarCep(valores.cep);
      if (!r) { alert('CEP não encontrado.'); return; }
      onChange('logradouro', r.logradouro || '');
      onChange('bairro', r.bairro || '');
      onChange('municipio', r.municipio || '');
      onChange('uf', r.uf || '');
      if (r.complemento && !valores.complemento) onChange('complemento', r.complemento);
    } finally { setBuscando(false); }
  }

  const inp: React.CSSProperties = {
    width: '100%', padding: '7px 10px', fontSize: 12, borderRadius: 4,
    border: '0.5px solid #ddd', boxSizing: 'border-box',
  };
  const lbl: React.CSSProperties = {
    fontSize: 11, color: '#555', display: 'block', marginBottom: 3, fontWeight: 600,
  };

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '180px auto 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={lbl}>CEP</label>
          <input value={valores.cep || ''} onChange={e => onChange('cep', e.target.value)}
            placeholder="00000-000" style={inp} />
        </div>
        <div style={{ alignSelf: 'end' }}>
          <button type="button" onClick={preencherViaCep} disabled={buscando}
            style={{ padding: '7px 14px', fontSize: 11, border: '0.5px solid #0930D0',
              background: '#fff', color: '#0930D0', borderRadius: 4,
              cursor: buscando ? 'wait' : 'pointer', height: 32 }}>
            {buscando ? '...' : '📮 ViaCEP'}
          </button>
        </div>
      </div>
      {comTipoLogradouro ? (
        <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <label style={lbl}>Tipo Logradouro</label>
            <select value={valores.tipo_logradouro || 'OUTROS'}
              onChange={e => onChange('tipo_logradouro', e.target.value)} style={inp}>
              {tiposLog.length === 0 && <option value="OUTROS">OUTROS</option>}
              {tiposLog.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Logradouro</label>
            <input value={valores.logradouro || ''}
              onChange={e => onChange('logradouro', e.target.value)}
              placeholder="Nome da rua/avenida" style={inp} />
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: 10 }}>
          <label style={lbl}>Logradouro</label>
          <input value={valores.logradouro || ''}
            onChange={e => onChange('logradouro', e.target.value)}
            placeholder="Rua / Avenida / Rodovia..." style={inp} />
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={lbl}>Número</label>
          <input value={valores.numero || ''}
            onChange={e => onChange('numero', e.target.value)} style={inp} />
        </div>
        <div>
          <label style={lbl}>Complemento</label>
          <input value={valores.complemento || ''}
            onChange={e => onChange('complemento', e.target.value)} style={inp} />
        </div>
        <div>
          <label style={lbl}>Bairro</label>
          <input value={valores.bairro || ''}
            onChange={e => onChange('bairro', e.target.value)} style={inp} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 10 }}>
        <div>
          <label style={lbl}>Município</label>
          <input value={valores.municipio || ''}
            onChange={e => onChange('municipio', e.target.value)} style={inp} />
        </div>
        <div>
          <label style={lbl}>UF</label>
          <input value={valores.uf || ''} maxLength={2}
            onChange={e => onChange('uf', e.target.value.toUpperCase())} style={inp} />
        </div>
      </div>
    </>
  );
}

// Subcomponente: lista dinâmica de linhas de atividade (passo 4). Cada linha
// é uma combinação técnica × específica × qtd × und.
function ListaLinhasAtividade({ linhas, onChange }: {
  linhas: ArtAtividadeLinha[];
  onChange: (novas: ArtAtividadeLinha[]) => void;
}) {
  const [catTec, setCatTec] = useState<CatAtividadeSlim[]>([]);
  const [catEsp, setCatEsp] = useState<CatAtividadeSlim[]>([]);
  const [unidades, setUnidades] = useState<Array<{ sigla: string; nome: string }>>([]);
  const [novaLinha, setNovaLinha] = useState<ArtAtividadeLinha>({
    cat_tecnica_id: null, cat_especifica_id: null,
    quantidade: 1, unidade: 'UN', descricao_complementar: null,
  });

  useEffect(() => {
    Promise.all([
      carregarCatAtividades('tecnica').then(setCatTec),
      carregarCatAtividades('especifica').then(setCatEsp),
      carregarUnidadesCrea().then(setUnidades),
    ]);
  }, []);

  function inserir() {
    if (!novaLinha.cat_tecnica_id && !novaLinha.cat_especifica_id) {
      alert('Selecione ao menos atividade técnica ou específica.');
      return;
    }
    const tec = catTec.find(t => t.id === novaLinha.cat_tecnica_id);
    const esp = catEsp.find(e => e.id === novaLinha.cat_especifica_id);
    onChange([...linhas, {
      ...novaLinha,
      cat_tecnica_nome: tec?.nome || null,
      cat_especifica_nome: esp?.nome || null,
    }]);
    setNovaLinha({ cat_tecnica_id: null, cat_especifica_id: null, quantidade: 1,
      unidade: 'UN', descricao_complementar: null });
  }

  function remover(idx: number) {
    onChange(linhas.filter((_, i) => i !== idx));
  }

  function setLinha(idx: number, campo: keyof ArtAtividadeLinha, val: any) {
    const nv = [...linhas];
    nv[idx] = { ...nv[idx], [campo]: val };
    if (campo === 'cat_tecnica_id') {
      const tec = catTec.find(t => t.id === val);
      nv[idx].cat_tecnica_nome = tec?.nome || null;
    }
    if (campo === 'cat_especifica_id') {
      const esp = catEsp.find(e => e.id === val);
      nv[idx].cat_especifica_nome = esp?.nome || null;
    }
    onChange(nv);
  }

  const inp: React.CSSProperties = {
    padding: '5px 7px', fontSize: 11, borderRadius: 3,
    border: '0.5px solid #ddd', boxSizing: 'border-box', width: '100%',
  };

  return (
    <div>
      {/* Form de inserir nova linha */}
      <div style={{ background: '#F4F6FB', border: '0.5px solid #d0d8e6', borderRadius: 6,
        padding: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#020D7F', marginBottom: 8 }}>
          ▸ Atividade
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
          <div>
            <label style={{ fontSize: 10, color: '#555', display: 'block', marginBottom: 2 }}>Atividade Técnica</label>
            <select value={novaLinha.cat_tecnica_id ?? ''}
              onChange={e => setNovaLinha({ ...novaLinha, cat_tecnica_id: e.target.value ? parseInt(e.target.value) : null })}
              style={inp}>
              <option value="">— Selecione —</option>
              {catTec.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 10, color: '#555', display: 'block', marginBottom: 2 }}>Atividade Específica</label>
            <select value={novaLinha.cat_especifica_id ?? ''}
              onChange={e => setNovaLinha({ ...novaLinha, cat_especifica_id: e.target.value ? parseInt(e.target.value) : null })}
              style={inp}>
              <option value="">— Selecione —</option>
              {catEsp.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 200px 1fr auto', gap: 10, alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: 10, color: '#555', display: 'block', marginBottom: 2 }}>Quantidade</label>
            <input type="number" step="0.001" value={novaLinha.quantidade}
              onChange={e => setNovaLinha({ ...novaLinha, quantidade: e.target.value })} style={inp} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: '#555', display: 'block', marginBottom: 2 }}>Unidade de Medida</label>
            <select value={novaLinha.unidade}
              onChange={e => setNovaLinha({ ...novaLinha, unidade: e.target.value })} style={inp}>
              {unidades.map(u => <option key={u.sigla} value={u.sigla}>{u.sigla} — {u.nome}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 10, color: '#555', display: 'block', marginBottom: 2 }}>Descrição complementar (opcional)</label>
            <input value={novaLinha.descricao_complementar || ''}
              onChange={e => setNovaLinha({ ...novaLinha, descricao_complementar: e.target.value || null })}
              placeholder="Ex.: aprox. 12 km de levantamento batimétrico"
              style={inp} />
          </div>
          <button type="button" onClick={inserir}
            style={{ padding: '7px 16px', fontSize: 11, fontWeight: 600,
              background: '#020D7F', color: '#fff', border: 'none', borderRadius: 4,
              cursor: 'pointer', height: 32 }}>
            + Inserir
          </button>
        </div>
      </div>

      {/* Lista de linhas existentes */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr style={{ background: '#F0F1F5', color: '#555' }}>
            <th style={{ padding: 8, textAlign: 'left', fontWeight: 600 }}>Atividade Técnica</th>
            <th style={{ padding: 8, textAlign: 'left', fontWeight: 600 }}>Atividade Específica</th>
            <th style={{ padding: 8, textAlign: 'right', width: 90, fontWeight: 600 }}>Qtd.</th>
            <th style={{ padding: 8, textAlign: 'left', width: 130, fontWeight: 600 }}>Unidade</th>
            <th style={{ padding: 8, textAlign: 'left', fontWeight: 600 }}>Descr. Compl.</th>
            <th style={{ width: 30 }}></th>
          </tr>
        </thead>
        <tbody>
          {linhas.length === 0 && (
            <tr><td colSpan={6} style={{ padding: 14, textAlign: 'center', color: '#aaa', fontStyle: 'italic' }}>
              Nenhuma atividade adicionada. Use o formulário acima.
            </td></tr>
          )}
          {linhas.map((l, i) => (
            <tr key={i} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
              <td style={{ padding: 4 }}>
                <select value={l.cat_tecnica_id ?? ''}
                  onChange={e => setLinha(i, 'cat_tecnica_id', e.target.value ? parseInt(e.target.value) : null)}
                  style={inp}>
                  <option value="">—</option>
                  {catTec.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </select>
              </td>
              <td style={{ padding: 4 }}>
                <select value={l.cat_especifica_id ?? ''}
                  onChange={e => setLinha(i, 'cat_especifica_id', e.target.value ? parseInt(e.target.value) : null)}
                  style={inp}>
                  <option value="">—</option>
                  {catEsp.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                </select>
              </td>
              <td style={{ padding: 4 }}>
                <input type="number" step="0.001" value={l.quantidade}
                  onChange={e => setLinha(i, 'quantidade', e.target.value)}
                  style={{ ...inp, textAlign: 'right' }} />
              </td>
              <td style={{ padding: 4 }}>
                <select value={l.unidade}
                  onChange={e => setLinha(i, 'unidade', e.target.value)} style={inp}>
                  {unidades.map(u => <option key={u.sigla} value={u.sigla}>{u.sigla}</option>)}
                </select>
              </td>
              <td style={{ padding: 4 }}>
                <input value={l.descricao_complementar || ''}
                  onChange={e => setLinha(i, 'descricao_complementar', e.target.value || null)}
                  style={inp} />
              </td>
              <td style={{ padding: 4, textAlign: 'center' }}>
                <button type="button" onClick={() => remover(i)}
                  title="Remover linha"
                  style={{ background: 'none', border: 'none', color: '#B91626',
                    cursor: 'pointer', fontSize: 13 }}>✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ModalArt({ contrato, art, duplicarDe, onSucesso, onClose }: {
  contrato: Contrato;
  art: ArtFull | null;          // null = nova
  duplicarDe?: ArtFull | null;  // se setado e art=null → preenche a partir desta ART
  onSucesso: () => void; onClose: () => void;
}) {
  const editando = !!art;
  const duplicando = !editando && !!duplicarDe;
  const [passo, setPasso] = useState<1|2|3|4|5>(1);
  // Quando duplicando, NÃO precisa carregar preset (já temos os dados).
  const [carregandoPreset, setCarregandoPreset] = useState(!editando && !duplicando);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [uploadingTipo, setUploadingTipo] = useState<'rascunho' | 'assinada' | null>(null);

  // Form state — agrega TUDO em um único objeto (campos string pra inputs;
  // booleans direto). Inicialização: editando → from art; duplicando → from
  // duplicarDe (com campos sensíveis resetados); novo → preset_art assíncrono.
  const [form, setForm] = useState<any>(() => {
    if (editando) return artToForm(art!);
    if (duplicando) return artToFormParaDuplicar(duplicarDe!, contrato);
    return formVazio();
  });
  // Linhas de atividade copiam (sem IDs — viram inserts novos quando salvar).
  const [linhas, setLinhas] = useState<ArtAtividadeLinha[]>(() => {
    if (editando) return art!.atividades_linhas || [];
    if (duplicando) return (duplicarDe!.atividades_linhas || []).map(l => ({
      ...l, id: undefined,  // sem ID → INSERT novo no salvar
    }));
    return [];
  });
  // Anexos NÃO duplicam (cada ART tem o seu).
  const [rascunhoKeys, setRascunhoKeys] = useState<string[]>(() => editando ? (art!.rascunho_s3_keys || []) : []);
  const [assinadaKeys, setAssinadaKeys] = useState<string[]>(() => editando ? (art!.assinada_s3_keys || []) : []);

  // Caches preenchidos via API
  const [usuarios, setUsuarios] = useState<UsuarioOp[]>([]);
  const [clientes, setClientes] = useState<any[]>([]);  // any pra acessar campos extras (endereco, cep, ...)
  // 2026-06-04: cadastro inline de cliente sem sair do ModalArt
  const [cadastrandoCli, setCadastrandoCli] = useState<null | 'contratante' | 'proprietario'>(null);
  // "Cadastrar nova empresa" exige `comercial.empresas.criar` no servidor
  // (05/10/2026). Falha ao carregar a matriz não vira "não pode".
  const { pode, erro: permsErro } = usePerms();
  const podeCriarEmpresa = permsErro || pode('comercial.empresas.criar');
  // 2026-06-05 (AT-IA-1b): importar ART de PDF via IA
  const [importarPdfAberto, setImportarPdfAberto] = useState(false);
  // 'relatorio' = preencher a ART inteira lendo o relatório técnico entregue.
  const [importarModo, setImportarModo] = useState<'art' | 'relatorio'>('art');
  const [opcoes, setOpcoes] = useState<OpcoesArt|null>(null);

  // RT pode ser usuário do sistema OU externo (não-usuário). Quando externo,
  // o dropdown do passo 1 fica como "Outro RT externo" e os campos rt_*
  // ficam todos manualmente editáveis.
  const [rtModoExterno, setRtModoExterno] = useState(false);

  // Carrega caches + preset (se nova). Cliente list foi expandida pra trazer
  // endereco/bairro/cep — usado no autofill do contratante.
  // forceRefresh=true bypassa o cache de 10min — garante que edições recentes
  // no cadastro do cliente (endereço, CEP, contato) já apareçam no autofill.
  useEffect(() => {
    Promise.all([
      carregarUsuariosOp().then(setUsuarios),
      carregarClientesCompletoCacheOuApi(true).then(setClientes),
      carregarOpcoesArt().then(setOpcoes),
    ]);
    if (!editando && !duplicando) {
      (async () => {
        try {
          const token = await getToken();
          const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'op_preset_art', contrato_id: contrato.id },
            { headers: { Authorization: token } });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          if (d?.error) {
            setErro('Não foi possível carregar dados do contrato. ' + d.error);
          } else {
            setForm((prev: any) => ({ ...prev, ...presetToForm(d) }));
          }
        } catch (e: any) {
          setErro('Erro ao carregar dados do contrato: ' + (e.response?.data?.error || e.message));
        } finally { setCarregandoPreset(false); }
      })();
    } else if (duplicando) {
      // Estado já vem populado pelo artToFormParaDuplicar — só não fica
      // travado no spinner.
      setCarregandoPreset(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Quando lista de usuários e contratante já carregaram, autofill do contratante
  // (preencher campos de endereço a partir do cadastro do cliente) — só pra
  // ARTs novas, e só na primeira vez que o cliente fica disponível.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!editando && clientes.length > 0 && form.contratante_cliente_id) {
      autofillContratante(form.contratante_cliente_id);
    }
  }, [clientes.length]);

  // Detecta RT externo após carregar usuários (em modo edit ou após preset).
  useEffect(() => {
    if (usuarios.length === 0) return;
    const ehUser = !!form.rt_email && usuarios.some(u => u.email.toLowerCase() === String(form.rt_email).toLowerCase());
    setRtModoExterno(!!form.rt_nome && !ehUser);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuarios.length]);

  // Identificador interno (template ART-[contrato]-[RT]) — atualiza enquanto
  // o usuário ainda não digitou um número CREA "real" (que costuma ser só
  // dígitos). Só roda em modo "nova".
  useEffect(() => {
    if (editando) return;
    setForm((prev: any) => {
      const atual = String(prev.numero || '').trim();
      // Se já tem um número CREA real (apenas dígitos), preserva
      if (/^\d{6,}$/.test(atual)) return prev;
      const nomeCompacto = String(prev.rt_nome || '')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^A-Za-z0-9]/g, '');
      const tpl = `ART-${contrato.numero}-${nomeCompacto || 'Profissional'}`;
      return prev.numero === tpl ? prev : { ...prev, numero: tpl };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.rt_nome, contrato.numero, editando]);

  function setCampo(k: string, v: any) {
    setForm((prev: any) => ({ ...prev, [k]: v }));
  }

  // RT — chave do dropdown:
  //   - email do usuário cadastrado
  //   - "__externo__" pra RT externo
  //   - "" pra "não escolhido"
  function rtSelecaoValor(): string {
    if (rtModoExterno) return '__externo__';
    if (form.rt_email) return form.rt_email;
    return '';
  }
  function selecionarRtPorChave(chave: string) {
    if (chave === '__externo__') {
      // Limpa pra que o usuário preencha todos os campos manualmente
      setRtModoExterno(true);
      setForm((prev: any) => ({
        ...prev,
        rt_email: '',
        rt_nome: '',
        rt_carteira: '',
        rt_rnp: '',
        rt_titulo_profissional: '',
      }));
      return;
    }
    if (!chave) {
      setRtModoExterno(false);
      setForm((prev: any) => ({
        ...prev, rt_email: '', rt_nome: '',
        rt_carteira: '', rt_rnp: '', rt_titulo_profissional: '',
      }));
      return;
    }
    setRtModoExterno(false);
    const u = usuarios.find(x => x.email.toLowerCase() === chave.toLowerCase());
    // Autofill carteira/RNP/título do cadastro do usuário em Admin.
    // SEMPRE sobrescreve quando troca pra outro usuário cadastrado (cobre o
    // caso de trocar de RT no meio da edição). Se o usuário não tem CREA
    // cadastrado, limpa os campos pra ficar visível que falta cadastrar.
    setForm((prev: any) => ({
      ...prev,
      rt_email: chave,
      rt_nome: u?.nome || '',
      rt_carteira: u?.crea || '',
      rt_rnp: u?.crea_rnp || '',
      rt_titulo_profissional: u?.crea_titulo || '',
    }));
  }

  // Empresa Executante: dropdown 2 opções. Auto-preenche nr_reg quando
  // empresa principal selecionada (a partir do cadastro Hydrodata).
  function selecionarEmpresaExecutante(nome: string) {
    if (nome === 'Nenhuma Empresa' || !nome) {
      setForm((prev: any) => ({ ...prev,
        empresa_executante_nome: 'Nenhuma Empresa',
        empresa_executante_nr_reg: null,
      }));
      return;
    }
    const ep = opcoes?.empresa_principal;
    setForm((prev: any) => ({ ...prev,
      empresa_executante_nome: nome,
      empresa_executante_nr_reg: (ep && ep.nome === nome) ? (ep.nr_reg || '') : prev.empresa_executante_nr_reg,
    }));
  }

  // Helpers de cópia rápida (passo 3)
  function copiarContratanteParaProprietario() {
    setCampo('proprietario_cliente_id', form.contratante_cliente_id);
  }
  function copiarEnderecoContratanteParaObra() {
    setForm((prev: any) => ({
      ...prev,
      obra_cep:         prev.contratante_cep         || prev.obra_cep,
      obra_logradouro:  prev.contratante_logradouro  || prev.obra_logradouro,
      obra_numero:      prev.contratante_numero      || prev.obra_numero,
      obra_complemento: prev.contratante_complemento || prev.obra_complemento,
      obra_bairro:      prev.contratante_bairro      || prev.obra_bairro,
      obra_municipio:   prev.contratante_municipio   || prev.obra_municipio,
      obra_uf:          prev.contratante_uf          || prev.obra_uf,
    }));
  }

  // Auto-preenche TODOS os campos de endereço/contato do contratante a partir
  // do cadastro do cliente (comercial.clientes).
  //
  // Modo:
  //   - sobrescrever=false (default): só preenche campos vazios — usado no
  //     mount inicial pra não apagar coisa que veio do preset/art salva.
  //   - sobrescrever=true: força a usar o endereço do cliente recém-selecionado
  //     — usado quando o usuário troca o cliente no dropdown (escolha explícita
  //     significa "quero o endereço dele aqui").
  function autofillContratante(clienteId: number, sobrescrever = false) {
    const c = clientes.find(x => x.id === clienteId);
    if (!c) return;
    setForm((prev: any) => {
      const pick = (campo: string, valorCliente: any) =>
        sobrescrever ? (valorCliente || '') : (prev[campo] || valorCliente || '');
      return {
        ...prev,
        contratante_telefone:   pick('contratante_telefone',   c.contato_fone),
        contratante_cep:        pick('contratante_cep',        c.cep),
        contratante_logradouro: pick('contratante_logradouro', c.endereco),
        contratante_bairro:     pick('contratante_bairro',     c.bairro),
        contratante_municipio:  pick('contratante_municipio',  c.cidade),
        contratante_uf:         pick('contratante_uf',         c.uf),
      };
    });
  }

  // Mesmo padrão pro proprietário — mas hoje só temos `proprietario_cliente_id`
  // no schema da ART (sem campos de endereço separados). Mantido como hook
  // pra futura expansão; por ora delega ao callback abaixo.

  function selecionarClienteContratante(idStr: string) {
    const id = idStr ? parseInt(idStr) : null;
    setCampo('contratante_cliente_id', id);
    // Escolha explícita do dropdown → sobrescreve endereço com o do cliente.
    if (id) autofillContratante(id, true);
  }
  function selecionarClienteProprietario(idStr: string) {
    const id = idStr ? parseInt(idStr) : null;
    setCampo('proprietario_cliente_id', id);
    // Se a obra ainda está vazia, herda endereço do proprietário recém-escolhido
    // (caso mais comum: obra fica no endereço do proprietário). Não sobrescreve
    // se já há algo digitado em obra_logradouro.
    if (id) autofillObraDoCliente(id);
  }

  // Preenche o endereço da OBRA a partir do cadastro do cliente — usado quando
  // o usuário escolhe o proprietário e a obra ainda está vazia. Conservador:
  // só preenche os campos vazios (não sobrescreve nada que o usuário digitou).
  function autofillObraDoCliente(clienteId: number) {
    const c = clientes.find(x => x.id === clienteId);
    if (!c) return;
    setForm((prev: any) => ({
      ...prev,
      obra_cep:        prev.obra_cep        || c.cep      || '',
      obra_logradouro: prev.obra_logradouro || c.endereco || '',
      obra_bairro:     prev.obra_bairro     || c.bairro   || '',
      obra_municipio:  prev.obra_municipio  || c.cidade   || '',
      obra_uf:         prev.obra_uf         || c.uf       || '',
    }));
  }

  // Verifica se o cadastro do contratante atual está completo.
  function cadastroContratanteCompleto(): { completo: boolean; faltam: string[] } {
    const c = clientes.find(x => x.id === form.contratante_cliente_id);
    if (!c) return { completo: false, faltam: ['cliente'] };
    const faltam: string[] = [];
    if (!c.cnpj) faltam.push('CNPJ');
    if (!c.endereco) faltam.push('endereço');
    if (!c.cep) faltam.push('CEP');
    if (!c.bairro) faltam.push('bairro');
    if (!c.cidade) faltam.push('cidade');
    if (!c.uf) faltam.push('UF');
    if (!c.contato_fone) faltam.push('telefone');
    return { completo: faltam.length === 0, faltam };
  }

  // Upload de PDF (mesma lógica de antes, mas agora chamada do passo 5)
  async function uploadPdf(file: File, tipo: 'rascunho' | 'assinada') {
    if (file.type && file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Anexe apenas arquivos PDF.');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      alert('Arquivo maior que 25MB.');
      return;
    }
    setUploadingTipo(tipo);
    try {
      const token = await getToken();
      const r1 = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_upload_url_art',
          contrato_id: contrato.id, art_id: art?.id || null,
          tipo, filename: file.name, content_type: file.type || 'application/pdf' },
        { headers: { Authorization: token } });
      const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      if (!d1.upload_url || !d1.s3_key) { alert('Erro ao gerar URL de upload.'); return; }
      await axios.put(d1.upload_url, file,
        { headers: { 'Content-Type': file.type || 'application/pdf' } });
      if (tipo === 'rascunho') setRascunhoKeys(curr => [...curr, d1.s3_key]);
      else setAssinadaKeys(curr => [...curr, d1.s3_key]);
    } catch (e: any) {
      alert('Erro no upload: ' + (e.response?.data?.error || e.message));
    } finally { setUploadingTipo(null); }
  }

  function removerAnexo(tipo: 'rascunho' | 'assinada', idx: number) {
    if (!window.confirm('Remover este anexo?')) return;
    if (tipo === 'rascunho') setRascunhoKeys(curr => curr.filter((_, i) => i !== idx));
    else setAssinadaKeys(curr => curr.filter((_, i) => i !== idx));
  }

  // Validação por passo — habilita botão "Próximo"
  function podeAvancar(p: number): boolean {
    if (p === 1) return !!form.tipo_art && !!form.motivo_art;
    if (p === 2) return !!form.contratante_cliente_id;
    if (p === 3) return !!form.proprietario_cliente_id && !!form.inicio_art && !!form.fim_art && !!form.valor;
    if (p === 4) return linhas.length > 0;
    return true;
  }

  async function salvar() {
    setErro('');
    setSalvando(true);
    try {
      const token = await getToken();
      const payload: any = {
        contrato_id: contrato.id,
        ...formToPayload(form),
        rascunho_s3_keys: rascunhoKeys,
        assinada_s3_keys: assinadaKeys,
        atividades_linhas: linhas.map((l, i) => ({
          cat_tecnica_id: l.cat_tecnica_id,
          cat_especifica_id: l.cat_especifica_id,
          quantidade: parseFloat(String(l.quantidade)) || 1,
          unidade: l.unidade || 'UN',
          descricao_complementar: l.descricao_complementar || null,
          ordem: i,
        })),
      };
      if (editando) {
        await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_atualizar_art', id: art!.id, ...payload },
          { headers: { Authorization: token } });
      } else {
        await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_criar_art', ...payload },
          { headers: { Authorization: token } });
      }
      onSucesso();
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    } finally { setSalvando(false); }
  }

  // Estilos compartilhados
  const inp: React.CSSProperties = {
    width: '100%', padding: '7px 10px', fontSize: 12, borderRadius: 4,
    border: '0.5px solid #ddd', boxSizing: 'border-box',
  };
  const lbl: React.CSSProperties = {
    fontSize: 11, color: '#555', display: 'block', marginBottom: 3, fontWeight: 600,
  };

  const passos: Array<{ num: 1|2|3|4|5; label: string }> = [
    { num: 1, label: 'Tipo de ART' },
    { num: 2, label: 'Contratante' },
    { num: 3, label: 'Obra/Serviço' },
    { num: 4, label: 'Atividades' },
    { num: 5, label: 'Anexos & status' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', padding: 22, borderRadius: 8, width: '100%',
          maxWidth: 980, maxHeight: '92vh', overflow: 'auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 16, color: '#020D7F' }}>
            {editando ? `✏ Editar ART ${art?.numero || ''}`
              : duplicando ? `⎘ Duplicar de ${duplicarDe?.numero || 'ART'}`
              : '+ Nova ART (CREA-RS)'}
          </h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => { setImportarModo('relatorio'); setImportarPdfAberto(true); }} disabled={salvando}
              title="A IA lê o relatório técnico entregue e sugere a ART inteira, com atividades e quantidades"
              style={{ background: '#020D7F', border: '1px solid #020D7F', borderRadius: 4,
                color: '#fff', cursor: 'pointer', fontSize: 11, padding: '4px 10px',
                fontWeight: 600 }}>
              📄 Preencher pelo relatório
            </button>
            <button onClick={() => { setImportarModo('art'); setImportarPdfAberto(true); }} disabled={salvando}
              title="Usa IA pra extrair os campos de um PDF de ART do CREA-RS"
              style={{ background: '#f0f4ff', border: '1px solid #0930D0', borderRadius: 4,
                color: '#020D7F', cursor: 'pointer', fontSize: 11, padding: '4px 10px',
                fontWeight: 600 }}>
              🤖 Importar de PDF
            </button>
            <button onClick={onClose}
              style={{ background: 'none', border: 'none', fontSize: 18, color: '#888', cursor: 'pointer' }}>
              ✕
            </button>
          </div>
        </div>

        {/* Progress bar dos 5 passos — clicável quando ART já preenchida
            (editando/duplicando) ou pra voltar a passo já validado. */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 18 }}>
          {passos.map(p => {
            // Pode navegar livremente se já tem ART carregada (editando/dup)
            // ou se o passo alvo é ≤ atual (voltar é sempre OK) ou se todos
            // os passos entre o atual e o alvo estão validados.
            let navegavel = editando || duplicando || p.num <= passo;
            if (!navegavel) {
              navegavel = true;
              for (let n = passo; n < p.num; n++) {
                if (!podeAvancar(n as 1|2|3|4|5)) { navegavel = false; break; }
              }
            }
            return (
              <div key={p.num} style={{ flex: 1, cursor: navegavel ? 'pointer' : 'default' }}
                onClick={() => navegavel && setPasso(p.num)}
                title={navegavel ? `Ir para ${p.label}` : 'Complete os passos anteriores'}>
                <div style={{ height: 4, borderRadius: 3,
                  background: passo >= p.num ? '#0930D0' : '#e0e0e0', marginBottom: 4 }} />
                <div style={{ fontSize: 10,
                  color: passo === p.num ? '#020D7F' : navegavel ? '#0930D0' : '#999',
                  fontWeight: passo === p.num ? 700 : 500, textAlign: 'center',
                  textDecoration: navegavel && passo !== p.num ? 'underline' : 'none' }}>
                  {p.num}. {p.label}
                </div>
              </div>
            );
          })}
        </div>

        {carregandoPreset ? (
          <div style={{ textAlign: 'center', padding: 30, color: '#888', fontSize: 12 }}>
            Carregando dados do contrato…
          </div>
        ) : (
          <div style={{ minHeight: 360 }}>
            {/* ─── PASSO 1: TIPO DE ART ───────────────────────────────────── */}
            {passo === 1 && opcoes && (
              <div>
                <SecaoArt titulo="Tipo de ART">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div>
                      <label style={lbl}>Convênio</label>
                      <select value={form.convenio} onChange={e => setCampo('convenio', e.target.value)} style={inp}>
                        {opcoes.convenios.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>Tipo de ART *</label>
                      <select value={form.tipo_art} onChange={e => setCampo('tipo_art', e.target.value)} style={inp}>
                        {opcoes.tipos_art.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>Empresa Executante</label>
                      <select value={form.empresa_executante_nome || 'Nenhuma Empresa'}
                        onChange={e => selecionarEmpresaExecutante(e.target.value)} style={inp}>
                        <option value="Nenhuma Empresa">Nenhuma Empresa</option>
                        {opcoes.empresa_principal && (
                          <option value={opcoes.empresa_principal.nome}>{opcoes.empresa_principal.nome}</option>
                        )}
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>Nr.Reg empresa (CREA)</label>
                      <input value={form.empresa_executante_nr_reg || ''}
                        onChange={e => setCampo('empresa_executante_nr_reg', e.target.value)}
                        placeholder={form.empresa_executante_nome === 'Nenhuma Empresa' ? '—' : 'Auto-preenchido'}
                        disabled={form.empresa_executante_nome === 'Nenhuma Empresa'}
                        style={{ ...inp, background: form.empresa_executante_nome === 'Nenhuma Empresa' ? '#f5f5f5' : '#fff' }} />
                    </div>
                    <div>
                      <label style={lbl}>Participação Técnica</label>
                      <select value={form.participacao_tecnica}
                        onChange={e => setCampo('participacao_tecnica', e.target.value)} style={inp}>
                        {opcoes.participacoes_tecnicas.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>Motivo da ART *</label>
                      <select value={form.motivo_art} onChange={e => setCampo('motivo_art', e.target.value)} style={inp}>
                        {opcoes.motivos.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                  </div>
                </SecaoArt>

                <SecaoArt titulo="Responsável Técnico (RT)">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
                    <div>
                      <label style={lbl}>RT *</label>
                      <select value={rtSelecaoValor()} onChange={e => selecionarRtPorChave(e.target.value)} style={inp}>
                        <option value="">— escolha um responsável —</option>
                        {usuarios.map(u => (
                          <option key={u.email} value={u.email}>{u.nome} ({u.email})</option>
                        ))}
                        <option value="__externo__">+ Outro RT externo (não-usuário)</option>
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>Email do RT</label>
                      <input value={form.rt_email || ''}
                        onChange={e => setCampo('rt_email', e.target.value)}
                        disabled={!rtModoExterno && !!form.rt_email && usuarios.some(u => u.email === form.rt_email)}
                        placeholder={rtModoExterno ? 'email@externo.com (opcional)' : ''}
                        style={inp} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 10 }}>
                    <div>
                      <label style={lbl}>Nome completo do RT</label>
                      <input value={form.rt_nome || ''} onChange={e => setCampo('rt_nome', e.target.value)} style={inp} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={lbl}>Carteira CREA</label>
                      <input value={form.rt_carteira || ''}
                        onChange={e => setCampo('rt_carteira', e.target.value)}
                        placeholder="Ex.: RS214787" style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>RNP</label>
                      <input value={form.rt_rnp || ''}
                        onChange={e => setCampo('rt_rnp', e.target.value)}
                        placeholder="Ex.: 2215003898" style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Título profissional</label>
                      <input value={form.rt_titulo_profissional || ''}
                        onChange={e => setCampo('rt_titulo_profissional', e.target.value)}
                        placeholder="Ex.: Engenheiro Civil" style={inp} />
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: '#888', marginTop: 8, fontStyle: 'italic' }}>
                    Carteira / RNP / Título são preenchidos automaticamente a partir do cadastro do usuário em
                    <strong> Admin &gt; Usuários &gt; 🪪 Cadastro técnico (CREA)</strong>. Pra RT externo, preencha manualmente.
                  </div>
                </SecaoArt>

                <SecaoArt titulo="Identificação (interna)">
                  <div>
                    <label style={lbl}>Identificador interno da ART</label>
                    <input value={form.numero || ''} onChange={e => setCampo('numero', e.target.value)}
                      placeholder="Ex.: ART-HY26031-LucasRangel"
                      style={inp} />
                    <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                      Quando o CREA emitir o número oficial (ex.: <code>14387061</code>), substitua aqui.
                      Até lá, o sistema usa o template <code>ART-[contrato]-[RT]</code>.
                    </div>
                  </div>
                </SecaoArt>
              </div>
            )}

            {/* ─── PASSO 2: CONTRATANTE ──────────────────────────────────── */}
            {passo === 2 && (
              <SecaoArt titulo="Contratante">
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <button type="button"
                    onClick={() => setCampo('contratante_eh_orgao_publico', true)}
                    style={{ flex: 1, padding: '8px 12px', fontSize: 11, fontWeight: 600,
                      background: form.contratante_eh_orgao_publico ? '#020D7F' : '#fff',
                      color: form.contratante_eh_orgao_publico ? '#fff' : '#555',
                      border: '0.5px solid #ddd', borderRadius: 4, cursor: 'pointer' }}>
                    Órgão Público
                  </button>
                  <button type="button"
                    onClick={() => setCampo('contratante_eh_orgao_publico', false)}
                    style={{ flex: 1, padding: '8px 12px', fontSize: 11, fontWeight: 600,
                      background: !form.contratante_eh_orgao_publico ? '#020D7F' : '#fff',
                      color: !form.contratante_eh_orgao_publico ? '#fff' : '#555',
                      border: '0.5px solid #ddd', borderRadius: 4, cursor: 'pointer' }}>
                    Não é Órgão Público
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={lbl}>Contratante (cliente cadastrado)</label>
                    <AntSelect
                      showSearch
                      allowClear
                      placeholder="— escolha cliente —"
                      value={form.contratante_cliente_id ?? undefined}
                      onChange={(v) => selecionarClienteContratante(v == null ? '' : String(v))}
                      optionFilterProp="label"
                      style={{ width: '100%' }}
                      options={clientes.map(c => ({
                        value: c.id,
                        label: `${c.razao_social}${c.cnpj ? ` (${c.cnpj})` : ''}`,
                      }))}
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          {podeCriarEmpresa && (<>
                            <Divider style={{ margin: '6px 0' }} />
                            <AntButton type="link" icon={<PlusOutlined />} block
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => setCadastrandoCli('contratante')}>
                              Cadastrar nova empresa
                            </AntButton>
                          </>)}
                        </>
                      )} />
                  </div>
                  <div>
                    <label style={lbl}>Telefone</label>
                    <input value={form.contratante_telefone || ''}
                      onChange={e => setCampo('contratante_telefone', e.target.value)} style={inp} />
                  </div>
                </div>

                {/* Aviso de cadastro incompleto — só quando há contratante e
                    falta algo. Direciona pra Comercial > Clientes pra completar. */}
                {form.contratante_cliente_id && (() => {
                  const status = cadastroContratanteCompleto();
                  if (status.completo) return null;
                  return (
                    <div style={{ padding: 10, background: '#FFF8E8', border: '0.5px solid #F0D88E',
                      borderRadius: 4, marginBottom: 12, fontSize: 11, color: '#7A5500',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <div>
                        ⚠ <strong>Cadastro incompleto:</strong> falta {status.faltam.join(', ')}.
                        Você pode preencher manualmente abaixo, ou abrir o cadastro do cliente
                        em <strong>Comercial &gt; Clientes</strong> pra completar de vez.
                      </div>
                      <button type="button" onClick={() => {
                          // Configura módulo+página antes de abrir nova aba pra
                          // o App.tsx restaurar via localStorage no mount.
                          try {
                            localStorage.setItem('app:modulo', 'comercial');
                            localStorage.setItem('app:pagina', 'com-clientes');
                          } catch {}
                          window.open('/', '_blank');
                        }}
                        style={{ padding: '5px 12px', fontSize: 11, fontWeight: 600,
                          background: '#fff', border: '0.5px solid #B07500', color: '#7A5500',
                          borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        ↗ Editar cadastro
                      </button>
                    </div>
                  );
                })()}

                <BlocoEndereco
                  valores={{
                    cep: form.contratante_cep || '',
                    logradouro: form.contratante_logradouro || '',
                    numero: form.contratante_numero || '',
                    complemento: form.contratante_complemento || '',
                    bairro: form.contratante_bairro || '',
                    municipio: form.contratante_municipio || '',
                    uf: form.contratante_uf || '',
                  }}
                  onChange={(c, v) => setCampo('contratante_' + c, v)}
                />
              </SecaoArt>
            )}

            {/* ─── PASSO 3: OBRA/SERVIÇO ──────────────────────────────────── */}
            {passo === 3 && opcoes && (
              <div>
                <SecaoArt titulo="Proprietário">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, marginBottom: 4 }}>
                    <div>
                      <label style={lbl}>Proprietário (cliente) *</label>
                      <AntSelect
                        showSearch
                        allowClear
                        placeholder="— escolha cliente —"
                        value={form.proprietario_cliente_id ?? undefined}
                        onChange={(v) => selecionarClienteProprietario(v == null ? '' : String(v))}
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                        options={clientes.map(c => ({
                          value: c.id,
                          label: `${c.razao_social}${c.cnpj ? ` (${c.cnpj})` : ''}`,
                        }))}
                        dropdownRender={(menu) => (
                          <>
                            {menu}
                            {podeCriarEmpresa && (<>
                              <Divider style={{ margin: '6px 0' }} />
                              <AntButton type="link" icon={<PlusOutlined />} block
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setCadastrandoCli('proprietario')}>
                                Cadastrar nova empresa
                              </AntButton>
                            </>)}
                          </>
                        )} />
                    </div>
                    <div style={{ alignSelf: 'end' }}>
                      <button type="button" onClick={copiarContratanteParaProprietario}
                        title="Definir proprietário = contratante"
                        style={{ padding: '7px 12px', fontSize: 10, border: '0.5px solid #ddd',
                          background: '#fff', color: '#555', borderRadius: 4, cursor: 'pointer' }}>
                        ⇗ Igual ao contratante
                      </button>
                    </div>
                  </div>
                </SecaoArt>

                <SecaoArt titulo="Endereço da Obra/Serviço">
                  <div style={{ marginBottom: 8 }}>
                    <button type="button" onClick={copiarEnderecoContratanteParaObra}
                      style={{ padding: '5px 10px', fontSize: 10, border: '0.5px solid #ddd',
                        background: '#fff', color: '#555', borderRadius: 4, cursor: 'pointer' }}>
                      ⇗ Copiar endereço do contratante
                    </button>
                  </div>
                  <BlocoEndereco
                    comTipoLogradouro
                    valores={{
                      cep: form.obra_cep || '',
                      tipo_logradouro: form.obra_tipo_logradouro || 'OUTROS',
                      logradouro: form.obra_logradouro || '',
                      numero: form.obra_numero || '',
                      complemento: form.obra_complemento || '',
                      bairro: form.obra_bairro || '',
                      municipio: form.obra_municipio || '',
                      uf: form.obra_uf || '',
                    }}
                    onChange={(c, v) => setCampo('obra_' + c, v)}
                  />
                  <div style={{ marginTop: 12 }}>
                    <label style={lbl}>Descrição livre do local (alternativa quando não há endereço urbano formal)</label>
                    <input value={form.endereco_obra || ''}
                      onChange={e => setCampo('endereco_obra', e.target.value)}
                      placeholder="Ex.: Bacia hidrográfica do rio X, ou: Município Y zona rural"
                      style={inp} />
                  </div>
                </SecaoArt>

                <SecaoArt titulo="Informações do Contrato">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
                    <div>
                      <label style={lbl}>Finalidade</label>
                      <select value={form.finalidade || 'Outras Finalidades'}
                        onChange={e => setCampo('finalidade', e.target.value)} style={inp}>
                        {opcoes.finalidades.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>Valor de Contrato (R$) *</label>
                      <input type="number" step="0.01" value={form.valor || ''}
                        onChange={e => setCampo('valor', e.target.value)} style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Honorários (R$) <span style={{ color: '#888', fontWeight: 400 }}>(opcional)</span></label>
                      <input type="number" step="0.01" value={form.honorarios || ''}
                        onChange={e => setCampo('honorarios', e.target.value)} style={inp} />
                    </div>
                    <div></div>
                    <div>
                      <label style={lbl}>Data de Início *</label>
                      <input type="date" value={form.inicio_art || ''}
                        onChange={e => setCampo('inicio_art', e.target.value)} style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Previsão de Fim *</label>
                      <input type="date" value={form.fim_art || ''}
                        onChange={e => setCampo('fim_art', e.target.value)} style={inp} />
                    </div>
                  </div>
                  <div>
                    <label style={lbl}>Resumo do contrato (vai pro CREA)</label>
                    <textarea value={form.resumo_contrato || ''}
                      onChange={e => setCampo('resumo_contrato', e.target.value)}
                      placeholder="Resumo conciso das atividades técnicas previstas no contrato"
                      rows={3} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
                  </div>
                </SecaoArt>
              </div>
            )}

            {/* ─── PASSO 4: ATIVIDADES (linhas) ───────────────────────────── */}
            {passo === 4 && (
              <SecaoArt titulo="Atividades CREA">
                {/* Aviso pra ARTs com dados legacy (Notion) */}
                {(art?.cat_tecnicas?.length || 0) + (art?.cat_especificas?.length || 0) > 0 && linhas.length === 0 && (
                  <div style={{ padding: 10, background: '#FFF8E8', color: '#B07500',
                    borderRadius: 4, marginBottom: 12, fontSize: 11 }}>
                    ⚠ Esta ART tem atividades em formato legacy (importadas do Notion). Adicione abaixo as
                    linhas estruturadas (técnica × específica × qtd × und); ao salvar, o sistema migra pra
                    o novo formato.
                  </div>
                )}
                <ListaLinhasAtividade linhas={linhas} onChange={setLinhas} />
              </SecaoArt>
            )}

            {/* ─── PASSO 5: ANEXOS + STATUS + OBSERVAÇÃO ─────────────────── */}
            {passo === 5 && opcoes && (
              <div>
                <SecaoArt titulo="Status CREA">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
                    <div>
                      <label style={lbl}>Status CREA</label>
                      <select value={form.status_crea || 'rascunho_interno'}
                        onChange={e => setCampo('status_crea', e.target.value)} style={inp}>
                        {opcoes.status_crea.map(s => (
                          <option key={s.codigo} value={s.codigo}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ alignSelf: 'end' }}>
                      <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!form.assinado_govbr}
                          onChange={e => setCampo('assinado_govbr', e.target.checked)} />
                        ✓ Assinado com Gov.br
                      </label>
                    </div>
                    <div>
                      <label style={lbl}>Data registro CREA</label>
                      <input type="date" value={form.data_registro_crea || ''}
                        onChange={e => setCampo('data_registro_crea', e.target.value)} style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Data pagamento CREA</label>
                      <input type="date" value={form.data_pagamento_crea || ''}
                        onChange={e => setCampo('data_pagamento_crea', e.target.value)} style={inp} />
                    </div>
                  </div>
                </SecaoArt>

                <SecaoArt titulo="Anexos PDF">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    {/* Rascunho */}
                    <div>
                      <label style={lbl}>Rascunho da ART ({rascunhoKeys.length})</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
                        {rascunhoKeys.map((k, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6,
                            background: '#fff', border: '0.5px solid #ddd', borderRadius: 4,
                            padding: '4px 8px', fontSize: 10 }}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap', color: '#666' }} title={k}>📄 {k.split('/').pop()}</span>
                            <button onClick={() => removerAnexo('rascunho', i)}
                              style={{ background: 'none', border: 'none', color: '#B91626',
                                cursor: 'pointer', fontSize: 12 }}>✕</button>
                          </div>
                        ))}
                      </div>
                      <input type="file" accept="application/pdf,.pdf"
                        disabled={uploadingTipo !== null}
                        onChange={e => {
                          const f = e.target.files?.[0];
                          if (f) { uploadPdf(f, 'rascunho'); e.target.value = ''; }
                        }}
                        style={{ fontSize: 10, width: '100%' }} />
                      {uploadingTipo === 'rascunho' && (
                        <div style={{ fontSize: 10, color: '#0930D0', marginTop: 4 }}>Enviando…</div>
                      )}
                    </div>

                    {/* Assinada */}
                    <div>
                      <label style={lbl}>ART assinada ({assinadaKeys.length})</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
                        {assinadaKeys.map((k, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6,
                            background: '#E8F5EE', border: '0.5px solid #B5DCC4', borderRadius: 4,
                            padding: '4px 8px', fontSize: 10 }}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap', color: '#0a7f4a', fontWeight: 600 }} title={k}>
                              ✓ {k.split('/').pop()}
                            </span>
                            <button onClick={() => removerAnexo('assinada', i)}
                              style={{ background: 'none', border: 'none', color: '#B91626',
                                cursor: 'pointer', fontSize: 12 }}>✕</button>
                          </div>
                        ))}
                      </div>
                      <input type="file" accept="application/pdf,.pdf"
                        disabled={uploadingTipo !== null}
                        onChange={e => {
                          const f = e.target.files?.[0];
                          if (f) { uploadPdf(f, 'assinada'); e.target.value = ''; }
                        }}
                        style={{ fontSize: 10, width: '100%' }} />
                      {uploadingTipo === 'assinada' && (
                        <div style={{ fontSize: 10, color: '#0a7f4a', marginTop: 4 }}>Enviando…</div>
                      )}
                    </div>
                  </div>
                </SecaoArt>

                <SecaoArt titulo="Observação">
                  <textarea value={form.observacao || ''}
                    onChange={e => setCampo('observacao', e.target.value)}
                    placeholder="Notas livres (opcional)"
                    rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
                </SecaoArt>
              </div>
            )}
          </div>
        )}

        {/* Erro */}
        {erro && (
          <div style={{ padding: 10, background: '#FDECEC', color: '#B91626',
            borderRadius: 4, marginTop: 10, fontSize: 11 }}>
            ⚠ {erro}
          </div>
        )}

        {/* Footer: Cancelar / Anterior / Próximo / Salvar */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 14 }}>
          <button onClick={onClose} disabled={salvando}
            style={{ padding: '8px 16px', fontSize: 12, border: '0.5px solid #ddd',
              background: '#fff', color: '#555', borderRadius: 5, cursor: 'pointer' }}>
            ← Fechar
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {passo > 1 && (
              <button onClick={() => setPasso((p) => (p - 1) as any)} disabled={salvando}
                style={{ padding: '8px 16px', fontSize: 12, border: '0.5px solid #ddd',
                  background: '#fff', color: '#555', borderRadius: 5, cursor: 'pointer' }}>
                ← Anterior
              </button>
            )}
            {passo < 5 && (
              <button onClick={() => setPasso((p) => (p + 1) as any)}
                disabled={!podeAvancar(passo)}
                style={{ padding: '8px 22px', fontSize: 12, fontWeight: 600,
                  background: podeAvancar(passo) ? '#020D7F' : '#bbb', color: '#fff',
                  border: 'none', borderRadius: 5, cursor: podeAvancar(passo) ? 'pointer' : 'not-allowed' }}>
                Próximo →
              </button>
            )}
            {passo === 5 && (
              <button onClick={salvar} disabled={salvando || uploadingTipo !== null}
                style={{ padding: '8px 22px', fontSize: 12, fontWeight: 600,
                  background: salvando ? '#bbb' : '#0a7f4a', color: '#fff',
                  border: 'none', borderRadius: 5, cursor: salvando ? 'wait' : 'pointer' }}>
                {salvando ? 'Salvando...' : (editando ? '💾 Salvar alterações' : '+ Criar ART')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* AT-IA-1 (2026-06-05): importar ART de PDF via IA */}
      <ModalImportarArtPdf
        open={importarPdfAberto}
        modo={importarModo}
        contratoId={contrato.id}
        artId={art?.id}
        formAtual={form}
        onCancel={() => setImportarPdfAberto(false)}
        onAplicar={({ campos, linhas_atividade, pdf_s3_key, status_pdf }) => {
          setImportarPdfAberto(false);
          // Mescla campos no form
          const novoForm: any = { ...form };
          const atividadesTexto = campos.atividades;
          for (const [k, v] of Object.entries(campos)) {
            if (k === 'atividades') continue;
            if (v == null || v === '') continue;
            novoForm[k] = v;
          }
          // Backup: atividades em texto livre vai pra observacao se nao
          // veio mapeamento (AT-IA-2 falhou ou IA nao extraiu atividades)
          if (Array.isArray(atividadesTexto) && atividadesTexto.length
              && (!linhas_atividade || !linhas_atividade.length)) {
            const txt = atividadesTexto.map((a: any, i: number) => `${i+1}. ${a}`).join('\n');
            novoForm.observacao = (novoForm.observacao
              ? novoForm.observacao + '\n\n--- Atividades extraidas do PDF ---\n'
              : '--- Atividades extraidas do PDF ---\n') + txt;
          }
          // status_crea = o que usuario escolheu no modal de importar.
          // Sobrescreve qualquer status_crea que veio nos campos (raro IA preencher).
          if (status_pdf) novoForm.status_crea = status_pdf;
          setForm(novoForm);
          // Contratante escolhido pela leitura do relatório, mas sem o endereço
          // da Receita (consulta falhou): completa pelo cadastro, só o vazio.
          if (campos.contratante_cliente_id && !campos.contratante_cep) {
            autofillContratante(Number(campos.contratante_cliente_id), false);
          }
          // AT-IA-2: aplica linhas mapeadas pro catalogo no passo 4
          if (Array.isArray(linhas_atividade) && linhas_atividade.length) {
            // Marca cada linha como nova (sem id — vira INSERT no salvar)
            setLinhas([
              ...linhas,
              ...linhas_atividade.map((l: any) => ({ ...l, id: undefined })),
            ]);
          }
          // Anexa o PDF no slot certo (rascunho ou assinada)
          if (pdf_s3_key) {
            if (status_pdf === 'registrada' || status_pdf === 'paga') {
              setAssinadaKeys([...assinadaKeys, pdf_s3_key]);
            } else {
              setRascunhoKeys([...rascunhoKeys, pdf_s3_key]);
            }
          }
        }} />

      {/* Modal de cadastro inline de cliente — disparado pelos botões "+ Nova" */}
      {cadastrandoCli && (
        <ModalCadastrarCliente
          open
          tituloContexto={cadastrandoCli === 'contratante' ? 'Contratante' : 'Proprietário'}
          onCancel={() => setCadastrandoCli(null)}
          onCreated={async (novo) => {
            const escopo = cadastrandoCli;
            setCadastrandoCli(null);
            // Recarrega lista invalidando cache
            const lista = await carregarClientesCompletoCacheOuApi(true);
            setClientes(lista);
            // Auto-seleciona o cliente recém-criado no contexto certo
            if (escopo === 'contratante') {
              selecionarClienteContratante(String(novo.id));
            } else {
              selecionarClienteProprietario(String(novo.id));
            }
          }} />
      )}
    </div>
  );
}

// Subcomponente visual: bloco de seção com título uppercase azul.
function SecaoArt({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div style={{ border: '0.5px solid #e0e0e0', borderRadius: 6, padding: 14,
      marginBottom: 12, background: '#fafafa' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#020D7F',
        textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
        {titulo}
      </div>
      {children}
    </div>
  );
}

// ─── Helpers de transformação form ↔ ART/preset (puros) ────────────────
function formVazio(): any {
  return {
    // Cabeçalho fiscal — defaults CREA-RS
    tipo_art: 'Obra ou Serviço',
    convenio: 'Não é convênio',
    participacao_tecnica: 'Individual/Principal',
    motivo_art: 'Normal',
    entidade_classe: 'SEM INDICACAO DE ENTIDADE',
    empresa_executante_nome: '',
    empresa_executante_nr_reg: '',
    // RT
    rt_email: '', rt_nome: '',
    rt_carteira: '', rt_rnp: '', rt_titulo_profissional: '',
    // Vínculos
    contratante_cliente_id: null, proprietario_cliente_id: null,
    // Vigência
    inicio_art: '', fim_art: '',
    // Contratante estruturado
    contratante_eh_orgao_publico: false,
    contratante_cep: '', contratante_logradouro: '', contratante_numero: '',
    contratante_complemento: '', contratante_bairro: '',
    contratante_municipio: '', contratante_uf: '', contratante_telefone: '',
    // Obra estruturada
    obra_cep: '', obra_tipo_logradouro: 'OUTROS',
    obra_logradouro: '', obra_numero: '', obra_complemento: '',
    obra_bairro: '', obra_municipio: '', obra_uf: '',
    // Texto livre
    endereco_obra: '', resumo_contrato: '', observacao: '',
    // Valores
    finalidade: 'Outras Finalidades', valor: '', honorarios: '',
    // Identificação
    numero: '',
    // Status CREA
    status_crea: 'rascunho_interno', assinado_govbr: false,
    data_registro_crea: '', data_pagamento_crea: '',
  };
}

function presetToForm(preset: any): any {
  const f = formVazio();
  // Faz override só dos que vierem definidos
  const merge = (k: string) => { if (preset[k] != null) f[k] = preset[k]; };
  ['tipo_art','convenio','participacao_tecnica','motivo_art','entidade_classe',
   'empresa_executante_nome','empresa_executante_nr_reg',
   'rt_email','rt_nome','rt_carteira','rt_rnp','rt_titulo_profissional',
   'contratante_cliente_id','proprietario_cliente_id',
   'contratante_municipio','contratante_uf','contratante_telefone','contratante_logradouro',
   'finalidade','status_crea','assinado_govbr',
   'resumo_contrato'].forEach(merge);
  if (preset.inicio_art) f.inicio_art = String(preset.inicio_art).slice(0,10);
  if (preset.fim_art) f.fim_art = String(preset.fim_art).slice(0,10);
  if (preset.valor != null) f.valor = String(preset.valor);
  return f;
}

function artToForm(a: ArtFull): any {
  const f = formVazio();
  const copy = (k: keyof ArtFull) => { if ((a as any)[k] != null) (f as any)[k] = (a as any)[k]; };
  ([
    'numero','tipo_art','convenio','participacao_tecnica','motivo_art','entidade_classe',
    'empresa_executante_nome','empresa_executante_nr_reg',
    'rt_email','rt_nome','rt_carteira','rt_rnp','rt_titulo_profissional',
    'contratante_cliente_id','proprietario_cliente_id',
    'contratante_eh_orgao_publico',
    'contratante_cep','contratante_logradouro','contratante_numero','contratante_complemento',
    'contratante_bairro','contratante_municipio','contratante_uf','contratante_telefone',
    'obra_cep','obra_tipo_logradouro','obra_logradouro','obra_numero','obra_complemento',
    'obra_bairro','obra_municipio','obra_uf',
    'endereco_obra','resumo_contrato','observacao',
    'finalidade','status_crea','assinado_govbr',
  ] as Array<keyof ArtFull>).forEach(copy);
  if (a.inicio_art) f.inicio_art = String(a.inicio_art).slice(0,10);
  if (a.fim_art) f.fim_art = String(a.fim_art).slice(0,10);
  if (a.data_registro_crea) f.data_registro_crea = String(a.data_registro_crea).slice(0,10);
  if (a.data_pagamento_crea) f.data_pagamento_crea = String(a.data_pagamento_crea).slice(0,10);
  if (a.valor != null) f.valor = String(a.valor);
  if (a.honorarios != null) f.honorarios = String(a.honorarios);
  return f;
}

// Cópia de uma ART pra criar uma nova. Reseta os campos que são "específicos
// daquela emissão" (numero CREA, status, datas de registro/pgto, Gov.br) — o
// resto vira o ponto de partida do wizard. Anexos NÃO duplicam (lidados no
// state diretamente — começam vazios).
function artToFormParaDuplicar(a: ArtFull, contrato: Contrato): any {
  const f = artToForm(a);
  // Reseta o identificador interno pra o template novo
  const nomeCompacto = String(a.rt_nome || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]/g, '');
  f.numero = `ART-${contrato.numero}-${nomeCompacto || 'Profissional'}`;
  // Reseta status e datas CREA — esta ART ainda não foi emitida
  f.status_crea = 'rascunho_interno';
  f.assinado_govbr = false;
  f.data_registro_crea = '';
  f.data_pagamento_crea = '';
  return f;
}

// Limpa string vazia → null e força tipos no envio pro backend.
function formToPayload(f: any): any {
  const s = (v: any) => (v == null || String(v).trim() === '') ? null : String(v).trim();
  const n = (v: any) => (v == null || v === '') ? null : parseFloat(v);
  return {
    numero: s(f.numero),
    tipo_art: s(f.tipo_art), convenio: s(f.convenio),
    participacao_tecnica: s(f.participacao_tecnica),
    motivo_art: s(f.motivo_art), entidade_classe: s(f.entidade_classe),
    empresa_executante_nome: s(f.empresa_executante_nome),
    empresa_executante_nr_reg: s(f.empresa_executante_nr_reg),
    rt_email: s(f.rt_email), rt_nome: s(f.rt_nome),
    rt_carteira: s(f.rt_carteira), rt_rnp: s(f.rt_rnp),
    rt_titulo_profissional: s(f.rt_titulo_profissional),
    contratante_cliente_id: f.contratante_cliente_id || null,
    proprietario_cliente_id: f.proprietario_cliente_id || null,
    inicio_art: s(f.inicio_art), fim_art: s(f.fim_art),
    contratante_eh_orgao_publico: !!f.contratante_eh_orgao_publico,
    contratante_cep: s(f.contratante_cep), contratante_logradouro: s(f.contratante_logradouro),
    contratante_numero: s(f.contratante_numero), contratante_complemento: s(f.contratante_complemento),
    contratante_bairro: s(f.contratante_bairro), contratante_municipio: s(f.contratante_municipio),
    contratante_uf: s(f.contratante_uf), contratante_telefone: s(f.contratante_telefone),
    obra_cep: s(f.obra_cep), obra_tipo_logradouro: s(f.obra_tipo_logradouro),
    obra_logradouro: s(f.obra_logradouro), obra_numero: s(f.obra_numero),
    obra_complemento: s(f.obra_complemento), obra_bairro: s(f.obra_bairro),
    obra_municipio: s(f.obra_municipio), obra_uf: s(f.obra_uf),
    endereco_obra: s(f.endereco_obra), resumo_contrato: s(f.resumo_contrato),
    observacao: s(f.observacao),
    finalidade: s(f.finalidade), valor: n(f.valor), honorarios: n(f.honorarios),
    status_crea: s(f.status_crea) || 'rascunho_interno',
    assinado_govbr: !!f.assinado_govbr,
    data_registro_crea: s(f.data_registro_crea),
    data_pagamento_crea: s(f.data_pagamento_crea),
  };
}
